// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { phaseDateSubtext } from '@/components/phase-date-subtext';
import { Phase, PhaseIndicator } from '@/components/phase-indicator';
import {
  PHASE_CONFIG,
  POPhase,
  POStatus,
  getPhaseProgression,
  getTerminalPhases,
  parseStatus,
} from '@/purchase-order/constants/status';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

interface Props {
  currentStatus: POStatus;
  cancelledReason?: string | null;
  isThirdPartyEnabled: boolean;
  createdAt: DateTime;
  shippedAt?: DateTime | null;
  deliveredAt?: DateTime | null;
  receivedAt?: DateTime | null;
}

/**
 * Component that displays the current phase of a purchase order.
 * Shows a stepper-style indicator with phases based on 3PL status.
 * With 3PL: Draft → Awaiting 3PL → Open → Shipped → Delivered → Received
 * Without 3PL: Draft → Open → Shipped → Delivered → Received
 */
export function PurchaseOrderPhaseIndicator(props: Props) {
  const {
    currentStatus,
    cancelledReason,
    isThirdPartyEnabled,
    createdAt,
    shippedAt,
    deliveredAt,
    receivedAt,
  } = props;

  const { phase: baseStatus, modifier } = parseStatus(currentStatus);

  const phases = useMemo(() => {
    const phaseDates: Partial<Record<POPhase, DateTime | null>> = {
      draft: createdAt,
      shipped: shippedAt,
      delivered: deliveredAt,
      received: receivedAt,
    };

    return getPhaseProgression(isThirdPartyEnabled).map(
      (status, index): Phase<POPhase> => ({
        status,
        label:
          status === baseStatus && modifier === 'partial'
            ? `Partially ${PHASE_CONFIG[status].label.toLowerCase()}`
            : PHASE_CONFIG[status].label,
        number: index + 1,
        isPartial: status === baseStatus && modifier === 'partial',
        subtext: phaseDateSubtext(phaseDates[status]),
      })
    );
  }, [
    isThirdPartyEnabled,
    baseStatus,
    modifier,
    createdAt,
    shippedAt,
    deliveredAt,
    receivedAt,
  ]);

  return (
    <PhaseIndicator
      phases={phases}
      currentStatus={baseStatus}
      cancelledStatus="cancelled"
      terminalStatuses={getTerminalPhases()}
      cancelledReason={cancelledReason}
    />
  );
}
